import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useFacilityToday } from "@/hooks/use-facility-today";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { AdoptChargesDialog } from "./-components/adopt-charges-dialog";
import { ChargeDetailsDialog } from "./-components/charge-details-dialog";
import { ChargeTable } from "./-components/charge-table";
import { DeleteChargeDialog } from "./-components/delete-charge-dialog";
import { CreateChargeDialog } from "./-components/create-charge-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { orpc } from "@/lib/orpc";
import { optionalMonth, optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { moneyReaders } from "@/lib/query/query-readers";
import { fromCents, errorWords } from "@alm/domain";

const STATUS_PARAMS = ["all", "active", "paused", "completed", "canceled"] as const;
type ChargeStatus = "running" | (typeof STATUS_PARAMS)[number];
type ChargesSearch = { period?: string; status?: (typeof STATUS_PARAMS)[number] };

export const Route = createFileRoute("/_authenticated/_financials/charges/")({
  validateSearch: (search: Record<string, unknown>): ChargesSearch => ({
    period: optionalMonth(search.period),
    status: optionalOneOf(STATUS_PARAMS, search.status),
  }),
  component: ChargesBoard,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** The month's recurring money: which residents have a charge in force, what it bills, and which of
 * them have not been invoiced yet. */
function ChargesBoard() {
  const { facilityId } = useFacility();
  const today = useFacilityToday(facilityId);
  const canWrite = useCan("financial:manage");
  const queryClient = useQueryClient();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ChargesSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const period = params.period ?? today.slice(0, 7);
  const setPeriod = (next: string) =>
    patch({ period: next === today.slice(0, 7) ? undefined : next });
  const [adopting, setAdopting] = useState(false);
  const status: ChargeStatus = params.status ?? "running";
  const setStatus = (next: ChargeStatus) =>
    patch({ status: next === "running" ? undefined : next });
  const [ending, setEnding] = useState<{ id: string; name: string; label: string } | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const overview = useQuery(
    orpc.financials.charges.overview.queryOptions({
      input: { facilityId, period, ...(status === "running" ? {} : { status }) },
    }),
  );

  const bill = useMutation(
    orpc.financials.charges.billPeriod.mutationOptions({
      onSuccess: async (result) => {
        toast.success(result.created ? "Invoice raised" : "That period was already billed");
        await invalidateKeys(queryClient, moneyReaders());
      },
      onError: fail,
    }),
  );

  /* Ending a series is how a resident stops being billed when they move out,
     and there was nowhere in the app to do it. */
  const endSeries = useMutation(
    orpc.financials.charges.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Series ended. Nothing more will be raised from it.");
        await invalidateKeys(queryClient, moneyReaders());
      },
      onError: fail,
    }),
  );

  const pause = useMutation(
    orpc.financials.charges.update.mutationOptions({
      onSuccess: async (charge) => {
        toast.success(charge.status === "paused" ? "Charge paused" : "Charge resumed");
        await invalidateKeys(queryClient, moneyReaders());
      },
      onError: fail,
    }),
  );

  const [creating, setCreating] = useState(false);
  const counts = overview.data?.counts;
  const items = overview.data?.items ?? [];

  const month = new Date(`${period}-15T00:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const filtered = status !== "running";
  const meta = [
    filtered ? `${month}, filtered` : month,
    counts?.behind ? `${counts.behind} behind` : null,
    counts?.paused ? `${counts.paused} paused` : null,
  ]
    .filter(Boolean)
    .join(", ");
  const money = overview.data?.money;

  return (
    <>
      <PageHeader
        title="Charge series"
        count={overview.isPending ? undefined : items.length}
        meta={overview.isPending ? "Loading…" : meta}
        actions={
          canWrite ? (
            <>
              {/* Only for a single building: adoption reads one community's
                  residents and writes one community's charges. */}
              {facilityId === "all" ? null : (
                <Button variant="outline" onClick={() => setAdopting(true)}>
                  Put building on charges
                </Button>
              )}
              <Button onClick={() => setCreating(true)}>Create charge</Button>
            </>
          ) : null
        }
      />
      <CreateChargeDialog open={creating} onOpenChange={setCreating} />
      {facilityId === "all" ? null : (
        <AdoptChargesDialog open={adopting} onOpenChange={setAdopting} facilityId={facilityId} />
      )}

      {overview.isError ? (
        <ReadFailed
          message="Charges could not be loaded."
          error={overview.error}
          onRetry={() => void overview.refetch()}
          retrying={overview.isFetching}
        />
      ) : overview.isPending ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <StatTiles>
          <StatTile
            label="Past due"
            value={fromCents(money?.pastDueCents ?? 0)}
            tone={(money?.pastDueCents ?? 0) > 0 ? "crit" : undefined}
          />
          <StatTile label="Unpaid" value={fromCents(money?.unpaidCents ?? 0)} />
          <StatTile label="Charged" value={fromCents(money?.chargedCents ?? 0)} />
          <StatTile label="Paid" value={fromCents(money?.paidCents ?? 0)} />
          <StatTile label="In transit" value={fromCents(money?.processingCents ?? 0)} />
        </StatTiles>
      )}

      {overview.isError ? null : overview.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <ChargeTable
          items={items}
          period={period}
          canWrite={canWrite}
          billing={bill.isPending}
          onBill={(chargeId) => bill.mutate({ chargeId, period })}
          onView={setViewing}
          onPauseToggle={(charge) =>
            pause.mutate({
              chargeId: charge.id,
              status: charge.status === "active" ? "paused" : "active",
            })
          }
          onEnd={setEnding}
          onDelete={setDeleting}
          trailing={
            <>
              <div className="flex items-center gap-2 max-sm:w-full">
                <Label className="text-muted-foreground" htmlFor="charges-period">
                  Period
                </Label>
                <Input
                  id="charges-period"
                  type="month"
                  value={period}
                  onChange={(event) => setPeriod(event.target.value || today.slice(0, 7))}
                  className="w-40 max-sm:flex-1"
                />
              </div>
              <Label htmlFor="charges-status" className="sr-only">
                Status
              </Label>
              <OptionSelect
                id="charges-status"
                className="w-full sm:w-44"
                value={status}
                onChange={(event) => setStatus(event.target.value as typeof status)}
              >
                <SelectOption value="running">Active and paused</SelectOption>
                <SelectOption value="all">Every status</SelectOption>
                <SelectOption value="active">Active</SelectOption>
                <SelectOption value="paused">Paused</SelectOption>
                <SelectOption value="completed">Completed</SelectOption>
                <SelectOption value="canceled">Canceled</SelectOption>
              </OptionSelect>
            </>
          }
          empty={
            status !== "running" ? (
              <EmptyState
                title="No charges match"
                description="No charge has this status in the period."
                action={
                  <Button variant="outline" size="sm" onClick={() => setStatus("running")}>
                    Clear filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No charges in force"
                description="A charge is what bills a resident every month."
              />
            )
          }
        />
      )}
      {viewing ? (
        <ChargeDetailsDialog
          chargeId={viewing}
          canWrite={canWrite}
          onOpenChange={(open) => (open ? null : setViewing(null))}
          onEnd={() => {
            const row = items.find((item) => item.id === viewing);
            setViewing(null);
            if (row) setEnding({ id: row.id, name: row.residentName, label: row.residentName });
          }}
          onDelete={() => {
            setDeleting(viewing);
            setViewing(null);
          }}
        />
      ) : null}
      {deleting ? (
        <DeleteChargeDialog
          chargeId={deleting}
          open
          onOpenChange={(open) => (open ? null : setDeleting(null))}
        />
      ) : null}
      {ending ? (
        <RemoveConfirm
          title={`End the charge for ${ending.name}?`}
          confirmLabel="End charge"
          description={`${ending.label}. Invoices already raised stay exactly as they are; nothing further will be billed from it.`}
          onConfirm={() => endSeries.mutate({ chargeId: ending.id, status: "canceled" })}
          onClose={() => setEnding(null)}
        />
      ) : null}
    </>
  );
}
